import React from 'react';
import { motion } from 'framer-motion';
import { CJMSAnalytics, CJMS_STAGES } from '../api/cjmsApi';

interface JourneyDashboardProps {
    analytics: CJMSAnalytics;
    totalClients: number;
    activeFilter?: string;
    onStageClick: (stage: string) => void;
}

export const JourneyDashboard: React.FC<JourneyDashboardProps> = ({
    analytics, totalClients, activeFilter = 'all', onStageClick
}) => {
    return (
        <div className="p-6 bg-white rounded-xl shadow-sm border border-slate-200">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-slate-800">Pipeline Distribution</h3>
                {activeFilter !== 'all' && (
                    <span className="text-xs text-indigo-600 font-medium">
                        Filtered to: {activeFilter}
                    </span>
                )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-9 gap-3">
                {CJMS_STAGES.map((stage) => {
                    const count = analytics.stageDistribution[stage] || 0;
                    const percentage = totalClients > 0 ? (count / Math.max(totalClients, 1)) * 100 : 0;
                    const isBottleneck = analytics.bottlenecks.includes(stage);
                    const isActive = activeFilter === stage;

                    return (
                        <motion.div
                            key={stage}
                            whileHover={{ y: -4 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => onStageClick(stage)}
                            className={`cursor-pointer p-3 rounded-lg border-2 transition-colors ${
                                isActive
                                ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-100'
                                : isBottleneck
                                ? 'border-red-300 bg-red-50'
                                : 'border-slate-100 bg-slate-50 hover:bg-indigo-50 hover:border-indigo-200'
                            }`}
                        >
                            <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider leading-tight min-h-[28px]">
                                {stage}
                            </div>
                            <div className="text-2xl font-bold text-slate-900 mt-1">{count}</div>
                            <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${percentage}%` }}
                                    className={`h-full ${
                                        isBottleneck ? 'bg-red-500' :
                                        isActive ? 'bg-indigo-500' : 'bg-indigo-400'
                                    }`}
                                />
                            </div>
                            {isBottleneck && (
                                <div className="mt-1.5 text-[9px] text-red-600 font-bold uppercase">Bottleneck</div>
                            )}
                        </motion.div>
                    );
                })}
            </div>

            <div className="mt-6 flex items-center gap-4 p-4 bg-indigo-50 rounded-lg">
                <div className="text-sm text-indigo-800 font-medium">
                    Overall Pipeline Health:
                    <span className="ml-2 font-bold text-lg">{Math.round(analytics.pipelineHealth)}%</span>
                </div>
                <div className="flex-1 h-2 bg-indigo-200 rounded-full overflow-hidden">
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${analytics.pipelineHealth}%` }}
                        className="h-full bg-indigo-600"
                    />
                </div>
            </div>
        </div>
    );
};
